/**
 * Theme Resolver - Automatically selects theme based on user context
 */

import { ThemeConfig, ThemeSelection } from './types';
import { getTheme, DEFAULT_THEME_NAME } from './themes';

// Company to theme mapping
const COMPANY_THEME_MAP: Record<string, string> = {
  'Silver Falls Capital': 'silver-falls-capital',
  'Sportworks': 'sportworks',
  'Sportworks Global LLC': 'sportworks',
  'sportworks': 'sportworks',
  'sportworks.com': 'sportworks',
  // Add more company mappings as needed
};

// Domain to theme mapping
const DOMAIN_THEME_MAP: Record<string, string> = {
  'silverfalls.capital': 'silver-falls-capital',
  'sfc.com': 'silver-falls-capital',
  'sportworks.com': 'sportworks',
  'sportworksglobal.com': 'sportworks',
  // Add more domain mappings as needed
};

/**
 * Resolves the appropriate theme based on user information
 */
export function resolveThemeForUser(user: {
  email?: string;
  companyName?: string;
  department?: string;
  jobTitle?: string;
  [key: string]: any;
}): ThemeConfig {
  // If no user information, return default theme
  if (!user) {
    return getTheme(DEFAULT_THEME_NAME);
  }

  // Try to resolve by company name
  if (user.companyName) {
    const themeId = findThemeByCompanyName(user.companyName);
    if (themeId) {
      return getTheme(themeId);
    }
  }

  // Try to resolve by email domain
  if (user.email) {
    const themeId = findThemeByEmailDomain(user.email);
    if (themeId) {
      return getTheme(themeId);
    }
  }

  // Return default theme
  return getTheme(DEFAULT_THEME_NAME);
}

/**
 * Find theme by company name (case-insensitive partial matching)
 */
function findThemeByCompanyName(companyName: string): string | null {
  const normalizedCompanyName = companyName.toLowerCase().trim();
  
  // Exact match first
  for (const [company, themeId] of Object.entries(COMPANY_THEME_MAP)) {
    if (company.toLowerCase() === normalizedCompanyName) {
      return themeId;
    }
  }
  
  // Partial match (company name contains keyword)
  for (const [company, themeId] of Object.entries(COMPANY_THEME_MAP)) {
    if (normalizedCompanyName.includes(company.toLowerCase()) || 
        company.toLowerCase().includes(normalizedCompanyName)) {
      return themeId;
    }
  }
  
  return null;
}

/**
 * Find theme by email domain
 */
function findThemeByEmailDomain(email: string): string | null {
  try {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return null;

    // Exact domain match
    const themeId = DOMAIN_THEME_MAP[domain];
    if (themeId) {
      return themeId;
    }

    // Check for subdomain matches
    for (const [mappedDomain, mappedThemeId] of Object.entries(DOMAIN_THEME_MAP)) {
      if (domain.endsWith(mappedDomain)) {
        return mappedThemeId;
      }
    }

    return null;
  } catch (error) {
    console.warn('Error parsing email domain:', error);
    return null;
  }
}

/**
 * Get theme name for display in UI
 */
export function getThemeDisplayName(themeSelection: ThemeSelection): string {
  switch (themeSelection) {
    case 'silver-falls-capital':
      return 'Silver Falls Capital';
    case 'sportworks':
      return 'Sportworks';
    case 'auto':
      return 'Auto (Based on Company)';
    default:
      return 'Silver Falls Capital';
  }
}

/**
 * Get available theme selections for UI
 */
export function getAvailableThemeSelections(): { id: ThemeSelection; name: string; description: string }[] {
  return [
    {
      id: 'auto',
      name: 'Auto (Based on Company)',
      description: 'Automatically selects the theme based on your company or email domain'
    },
    {
      id: 'silver-falls-capital',
      name: 'Silver Falls Capital',
      description: 'Pacific Northwest inspired color palette with sophisticated greys and ocean tones'
    },
    {
      id: 'sportworks',
      name: 'Sportworks',
      description: 'Professional bike parking and transit solutions theme with dynamic yellow and sophisticated grays'
    }
  ];
}

/**
 * Resolve theme selection to actual theme
 */
export function resolveThemeSelection(
  selection: ThemeSelection,
  user?: {
    email?: string;
    companyName?: string;
    [key: string]: any;
  }
): ThemeConfig {
  if (selection === 'auto') {
    return resolveThemeForUser(user || {});
  }
  return getTheme(selection);
}